import {memo} from 'react';

import styled from 'styled-components';

import {DropdownContainer} from '~/shared/components/DropdownButton';
import {flexCenter, flexCenterVertically, flexColumn} from '~/shared/theme/FlexLayout';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {body14Bold, body14Normal, smallRegular} from '~/shared/theme/typography';
import {SupportedLanguageType} from '~/shared/consts/commonConsts';
import {getLocalizationService} from '~/shared/services/localisationService';
import Button from '~/shared/components/Button';
import {media} from '~/shared/theme/media';

import SelectedCategoryIcon from './checked-category.svg?react';
import {ITenbisForBusinessCategory} from './interface';

const StyledSelectedCategoryIcon = styled(SelectedCategoryIcon)`
  path {
    fill: ${({theme}) => theme.colors.contentBrand};
  }

  ${flipOnLTR`
    margin-right: auto;
  `}
`;

const StyledDropdownContainer = styled(DropdownContainer)<{isActiveCategory: boolean}>`
  & > button:first-child {
    ${body14Bold}
    border: none;
    color: ${({theme}) => theme.colors.supportOrange};
    width: auto;
    padding: 0;

    svg {
      path {
        fill: ${({theme}) => theme.colors.supportOrange};
      }
    }
  }
`;

const CategoriesContainer = styled.div.attrs({
  id: '10bisforbusiness-dropdown',
})`
  ${flexColumn}
  width: 247px;
  justify-content: unset;
  position: absolute;
  top: 0;
  padding: 16px 0;
  background-color: ${({theme}) => theme.colors.surface};
  border-radius: 12px;
  border: 1px solid ${({theme}) => theme.colors.gray500};
  box-shadow: ${({theme}) => theme.shadows.shadow1};
  gap: 8px;
  ${flipOnLTR`
    left: unset;
    right: 0;
  `}
`;

const Wrapper = styled.div`
  height: 32px;
  ${flipOnLTR`
    margin-left: 3px;
  `}
  ${media.minLargeMobile`
    ${flipOnLTR`
      margin-left: unset;
    `}
  `}
`;

const Category = styled.div<{isSelected: boolean}>`
  ${flexCenterVertically}
  justify-content: space-between;
  min-height: 38px;
  max-height: 38px;
  padding: 8px 16px;
  cursor: pointer;
  ${({isSelected, theme}) => isSelected && `background-color: ${theme.colors.gray800};`}

  &:hover {
    background: ${({theme}) => theme.colors.gray800};
  }
`;

const CategoryItem = styled.div`
  ${flexCenterVertically}
`;

const IconWrapper = styled.div`
  width: 32px;
  height: 32px;
  background: ${({theme}) => theme.colors.orange10};
  border-radius: 32px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  ${flexCenter};
`;

const Label = styled.div`
  ${body14Normal}
  ${flipOnLTR`
    margin-right: 16px;
  `}
`;

const ResetButtonWrapper = styled.div`
  ${flexCenter}
  justify-content: flex-end;
  height: 28px;
  padding: 0 16px 8px;
`;

const ResetButton = styled(Button)`
  color: ${({theme}) => theme.colors.surfacePrimaryAction};
  ${smallRegular}
`;

const TenBisForBusinessButton = ({selectedCategoriesNumber}: {selectedCategoriesNumber: number}) => {
  const {t} = getLocalizationService();
  const selectedCategoriesText = selectedCategoriesNumber ? ` (${selectedCategoriesNumber})` : '';

  return <>{`${t('tenbis_for_business')}${selectedCategoriesText}`}</>;
};

interface ViewProps {
  activeCategory?: ITenbisForBusinessCategory;
  isOpen?: boolean;
  currentLanguageKey: SupportedLanguageType['key'];
  categories: ITenbisForBusinessCategory[];
  closeDropdown: () => void;
  onReset: () => void;
  onSelect: (selectedCategory: ITenbisForBusinessCategory) => void;
  toggleDropdown: () => void;
}

const View = ({
  activeCategory,
  isOpen,
  currentLanguageKey,
  categories,
  closeDropdown,
  onReset,
  onSelect,
  toggleDropdown,
}: ViewProps) => {
  const {t} = getLocalizationService();
  const isActiveCategory = Boolean(activeCategory);

  const handleResetClick = () => {
    onReset();
    closeDropdown();
  };

  return (
    <Wrapper>
      {/* @ts-expect-error TODO: DropdownContainer -> needs to be rewritten as ts */}
      <StyledDropdownContainer
        isActiveCategory={isActiveCategory}
        isOpen={isOpen}
        buttonTitle={<TenBisForBusinessButton selectedCategoriesNumber={isActiveCategory ? 1 : 0} />}
        onDropdownClick={toggleDropdown}
        closeDropdown={closeDropdown}
      >
        <CategoriesContainer>
          {isActiveCategory && (
            <ResetButtonWrapper key="reset_button">
              <ResetButton onClick={handleResetClick}>{t('clear')}</ResetButton>
            </ResetButtonWrapper>
          )}
          {categories.map((category: any) => {
            const isSelected = activeCategory?.id === category.id;
            
            return (
              <Category key={category.id} onClick={() => onSelect(category)} isSelected={isSelected}>
                <CategoryItem>
                  <IconWrapper>
                    <ImageWithAlt src={category.iconUrl} alt={category.label[currentLanguageKey]} />
                  </IconWrapper>
                  <Label>{category.label[currentLanguageKey]}</Label>
                </CategoryItem>
                {isSelected && <StyledSelectedCategoryIcon />}
              </Category>
            );
          })}
        </CategoriesContainer>
      </StyledDropdownContainer>
    </Wrapper>
  );
};

export default memo(View);
